import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

const MAX_FLAGS = 10

const COLORS = {
  mattie: '#d42b2b',
  marieke: '#2b6ed4',
}

// Convert WGS84 lat/lng to SVG coordinates
// The SVG uses an equirectangular projection with 160% N/S stretching
// Geographic bounds: N: 53.7°, S: 50.6°, W: 3.1°E, E: 7.5°E
// SVG viewBox: 0 0 887.641 1000.512
const SVG_W = 887.641
const SVG_H = 1000.512
const LAT_N = 53.7
const LAT_S = 50.6
const LNG_W = 3.1
const LNG_E = 7.5

export function geoToSvg(lat, lng) {
  return {
    x: ((lng - LNG_W) / (LNG_E - LNG_W)) * SVG_W,
    y: ((LAT_N - lat) / (LAT_N - LAT_S)) * SVG_H,
  }
}

export { MAX_FLAGS, COLORS }

export const useGameStore = defineStore('game', () => {
  const rawChallenges = ref([])
  const rawSearches = ref([])
  const rawPlayers = ref([])

  const parseDate = (str) => DateTime.fromFormat(str, 'dd/MM/yyyy HH:mm:ss', { zone: 'Europe/Amsterdam' })

  // Filter out future-dated challenges
  const visibleChallenges = computed(() => {
    const now = DateTime.now()
    return rawChallenges.value.filter((c) => {
      if (!c.visible_at) return false
      return parseDate(c.visible_at) <= now
    })
  })
  // Filter out future-dated searches
  const visibleSearches = computed(() => {
    const now = DateTime.now()
    return rawSearches.value.filter((s) => {
      if (!s.visible_at) return false
      return parseDate(s.visible_at) <= now
    })
  })
  const allFlags = computed(() => [...visibleChallenges.value, ...visibleSearches.value])

  // Challenge flags sorted by visible_at for route line ordering
  const sortedChallenges = computed(() =>
    [...visibleChallenges.value].sort((a, b) => (a.visible_at || '').localeCompare(b.visible_at || ''))
  )

  // Players with their flags, count, image, and color
  const players = computed(() => {
    const playerMap = {}
    rawPlayers.value.forEach((p) => {
      const name = p.name?.toLowerCase()
      if (!name) return
      playerMap[name] = {
        name,
        imageUrl: p.image_url || '',
        color: COLORS[name] || '#888',
        flags: [],
      }
    })
    allFlags.value.forEach((f) => {
      const name = f.winner?.toLowerCase()
      if (playerMap[name]) {
        playerMap[name].flags.push(f)
      }
    })
    return playerMap
  })

  function player(name) {
    return players.value[name] || { name, imageUrl: '', color: '#888', flags: [] }
  }

  // Active flag selection (shared between scoreboard and map)
  const activeFlagPlayer = ref('')
  const activeFlagIndex = ref(-1)

  function selectFlag(playerName, index) {
    if (activeFlagPlayer.value === playerName && activeFlagIndex.value === index) {
      activeFlagPlayer.value = ''
      activeFlagIndex.value = -1
    } else {
      activeFlagPlayer.value = playerName
      activeFlagIndex.value = index
    }
  }

  function clearSelection() {
    activeFlagPlayer.value = ''
    activeFlagIndex.value = -1
  }

  function loadData({ challenges, searches, players: p }) {
    if (challenges) rawChallenges.value = challenges
    if (searches) rawSearches.value = searches
    if (p) rawPlayers.value = p
  }

  return {
    allFlags,
    sortedChallenges,
    players,
    player,
    activeFlagPlayer,
    activeFlagIndex,
    selectFlag,
    clearSelection,
    loadData,
  }
})
